/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {WarningsEnum} from '../warnings/warnings.enum';
import {InputMessagesService} from '../input/service/input-messages.service';
import {InputTextComponent} from '../input/input-text/input-text.component';
import {StatefulComponent} from '../stateful/stateful.component';
import {ChipInputState} from './models/chip-input.state';
import {ChipInputValueModel} from './models/chip-input-value.model';
import {ChipInputTypeEnum} from './models/chip-input-type.enum';
import {
    InputAutocompleteComponent,
    ResultTemplateContext
} from '../input/input-autocomplete/input-autocomplete.component';
import {InputComponent} from '../input/input/input.component';
import {ChipInputAutocompleteConfigurationModel} from './models/chip-input-autocomplete-configuration.model';
import {ChipInputConfigurationModel} from './models/chip-input-configuration.model';
import {Subject} from 'rxjs';
import {take} from 'rxjs/operators';

@Component({
    selector: 'app-chip-input',
    templateUrl: './chip-input.component.html',
    styleUrls: ['./chip-input.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ChipInputComponent<T> extends StatefulComponent<ChipInputState<T>> implements OnChanges {
    @Input() chipInputConfiguration: ChipInputConfigurationModel<T>;
    @Input() inputType: ChipInputTypeEnum = ChipInputTypeEnum.TEXT;
    @Input() autocompleteConfiguration: ChipInputAutocompleteConfigurationModel<T>;
    @Input() resultTemplate: TemplateRef<ResultTemplateContext>;
    @Input() tooltipTemplate: TemplateRef<T>;
    @Output() chipsValues: EventEmitter<ChipInputValueModel<T>> = new EventEmitter<ChipInputValueModel<T>>();
    @Output() addChip: EventEmitter<T> = new EventEmitter<T>();
    @Output() deleteChip: EventEmitter<T> = new EventEmitter<T>();
    @ViewChild('chipInputText') chipInputText: InputTextComponent;
    @ViewChild('chipInputAutocomplete') chipInputAutocomplete: InputAutocompleteComponent;
    @ViewChild('chipList') chipList: ElementRef;
    @ViewChild('chip') chip: ElementRef;
    public hasSelectedArrow = false;
    chipMessages = new Map<WarningsEnum, string[]>();
    @Input() addRequestEnd: Subject<boolean>;
    @Input() deleteRequestEnd: Subject<boolean>;
    public readonly INPUT_HEIGHT = 31;
    private readonly MAX_NUMBER_OF_CHIP_ROWS = 10;
    private readonly CHIP_HEIGHT = 23;

    get inputComponent(): InputComponent {
        return this.isAutocomplete ? this.chipInputAutocomplete : this.chipInputText;
    }

    get isAutocomplete(): boolean {
        return this.inputType === ChipInputTypeEnum.AUTOCOMPLETE && this.autocompleteConfiguration != null;
    }

    get baseDataCy(): string {
        const name = this.state?.configuration?.chipLabel?.replace(/\s/g, '-')
                .toLowerCase();

        return `chip-input-${name}`;
    }

    get titleDataCy(): string {
        return `${this.baseDataCy}-title`;
    }

    get addButtonDataCy(): string {
        return `${this.baseDataCy}-add-button`;
    }

    get inputAndButtonWrapperDataCy(): string {
        return `${this.baseDataCy}-input-and-button-wrapper`;
    }

    get chipDataCy(): string {
        return `${this.baseDataCy}-chip`;
    }

    get inputPlaceholder(): string {
        let placeholder = this.state.configuration.placeholder;

        if (placeholder == null) {
            placeholder = `Please, write a ${this.state.configuration?.chipLabel}`;

            if (this.state.configuration?.messageErrorExample != null) {
                placeholder += `, e.g. ${this.state.configuration?.messageErrorExample}`;
            }
        }

        return placeholder;
    }

    get selectedLabel(): string {
        return `Selected (${this.state?.chips.length})`;
    }

    get hasSelectedChips(): boolean {
        return this.state?.chips?.length > 0;
    }

    get selectedArrowClass(): string {
        const base = 'selected-button-arrow';
        return `${base} ${this.state.chipListIsCollapsed ? 'iwp-icon-gen_arrow_right' : 'iwp-icon-gen_arrow_down active'}`;
    }

    get showChipList(): boolean {
        return !this.state.chipListIsCollapsed && this.hasSelectedChips;
    }

    get inputLabel(): string {
        let label = this.state.configuration.inputLabel;
        if (label == null) {
            label = `Add a ${this.state.configuration.chipLabel}`;
        }
        return label;
    }

    constructor(private inputMessagesService: InputMessagesService,
                private cdr: ChangeDetectorRef) {
        super();
    }

    toggleHasSelectedArrow(): void {
        this.cdr.detectChanges();
        this.storeState({hasSelectedArrow: this.chipListHasVerticalOverflow()});
    }

    clickSelectedButton(): void {
        this.storeState({chipListIsCollapsed: !this.state.chipListIsCollapsed});
    }

    setChip(value: T): void {
        this.inputMessagesService.invalidInputValueError(this.inputComponent.inputControl,
                this.chipMessages, this.state.configuration?.chipLabel, this.state.configuration?.messageErrorExample);

        this.chipMessages = new Map(this.chipMessages);

        const errors = this.chipMessages.get(WarningsEnum.ERROR);
        if (errors == null || errors.length === 0) {
            if (typeof value === 'string') {
                value = (value as any).toUpperCase();
            }
            this.manageRepeatedChipsErrors(value);
        }
        this.chipsValues.emit({chips: this.state?.chips, isValid: this.inputComponent.isValid});
    }

    disableAddButton(): boolean {
        let disabled = false;
        if (!this.inputComponent?.isValid
                || this.hasMaxChipLength()
                || this.inputComponent?.inputControl?.value == null
                || this.inputComponent?.inputControl?.value?.length === 0
        ) {
            disabled = true;
        }
        return disabled;
    }

    hasMaxChipLength(): boolean {
        let maxReached = false;
        if (this.state.configuration?.maxChipsLimit != null) {
            maxReached = this.state?.chips?.length >= this.state.configuration?.maxChipsLimit;
        }
        return maxReached;
    }

    maxLengthTooltipMessage(): string {
        const label = this.state?.configuration?.maxChipsLimit > 1 ? `${this.state.configuration?.chipLabel}s`
                : this.state.configuration?.chipLabel;
        return `You can\'t add more than ${this.state.configuration?.maxChipsLimit} ${label}.`;
    }

    onChipAdd(): void {
        const chip = this.inputComponent?.inputControl?.value;
        this.addChip.emit(chip);
        if (chip != null) {
            if (this.addRequestEnd != null) {
                this.addRequestEnd.pipe(take(1))
                        .subscribe((success) => {
                            if (success) {
                                this.addNewChip(chip);
                            }
                        });
            } else {
                this.addNewChip(chip);
            }
        }
    }

    onChipDelete(value: T): void {
        this.deleteChip.emit(value);
        if (this.deleteRequestEnd != null) {
            this.deleteRequestEnd.pipe(take(1))
                    .subscribe((success) => {
                        if (success) {
                            this.deleteExistingChip(value);
                        }
                    });
        } else {
            this.deleteExistingChip(value);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['chipInputConfiguration']) {
            this.storeState({
                configuration: changes['chipInputConfiguration'].currentValue,
                chips: changes['chipInputConfiguration'].currentValue.initialChips,
                chipListIsCollapsed: false
            });
            this.toggleHasSelectedArrow();
            this.storeState({chipListIsCollapsed: this.state.hasSelectedArrow});
        }
    }

    chipListHeight(): { [key: string]: string } {
        return {'max-height': `${this.MAX_NUMBER_OF_CHIP_ROWS * this.CHIP_HEIGHT}px`};
    }

    private deleteExistingChip(chip: T): void {
        let currentChips = [...this.state?.chips];
        currentChips = currentChips.filter((item) => item !== chip);
        this.storeState({
            chips: currentChips
        });
        this.chipsValues.emit({chips: this.state.chips, isValid: this.inputComponent.isValid});
        this.toggleHasSelectedArrow();
    }

    private addNewChip(chip: T): void {
        if (this.state.chips == null) {
            this.storeState({chips: []});
        }
        const currentChips = [...this.state?.chips];

        if (this.inputComponent === this.chipInputText) {
            if (chip.constructor === String) {
                // Lint is being ignored because the cast of chip to string is always throwing an error.
                // The fact that the active input is chipInputText and that the chip.constructor is String, ensures us
                // that the chip value is in fact string and can proceed with the following code
                // @ts-ignore
                currentChips.push((chip as string).toUpperCase());
            }
        } else {
            currentChips.push(chip);
        }

        if (this.state.configuration.chipSortFunction != null) {
            currentChips.sort(this.state.configuration.chipSortFunction);
        }

        this.storeState({
            chips: currentChips
        });
        this.inputComponent?.deleteInputValue();
        this.chipsValues.emit({chips: this.state?.chips, isValid: this.inputComponent.isValid});
        this.toggleHasSelectedArrow();
    }

    private chipListHasVerticalOverflow(): boolean {
        this.cdr.detectChanges();
        return this.chipList?.nativeElement?.offsetHeight < this.chipList?.nativeElement?.scrollHeight;
    }

    private manageRepeatedChipsErrors(value: T): void {
        if (this.state?.chips?.includes(value)) {
            this.chipMessages.set(WarningsEnum.ERROR, [`That ${this.state.configuration?.chipLabel} was already added.`]);
            this.inputComponent.markAsInvalid();
        } else if (this.state.configuration?.excludedChipValues?.includes(value)) {
            this.chipMessages.set(WarningsEnum.ERROR,
                    [`That ${this.state.configuration?.chipLabel} is already associated to another `
                    + `${this.state.configuration?.groupLabel}.`]);
            this.inputComponent.markAsInvalid();
        } else {
            this.chipMessages.delete(WarningsEnum.ERROR);
        }
    }

}
